import { useEffect, useMemo, useState } from "react";
import { useRoute, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Download, Image as ImageIcon, Video as VideoIcon } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";

export default function PurchasedGallery() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const [, params] = useRoute("/purchased/:token");
  const token = params?.token || "";

  // Require gallery login/session before accessing purchased downloads
  const sessionRaw = typeof window !== 'undefined' ? localStorage.getItem("gallery_session") : null;
  if (!sessionRaw) {
    const next = encodeURIComponent(`/purchased/${token}`);
    navigate(`/gallery/login?next=${next}`);
    return null;
  }

  const validate = trpc.downloadTokens.validate.useQuery({ token }, { enabled: !!token });
  const links = trpc.downloadTokens.getDownloadLinks.useQuery({ token }, { enabled: !!token });

  const items = links.data?.downloadLinks || [] as Array<{ id: string; filename: string; url: string; type: "image" | "video"; previewUrl?: string | null; thumbUrl?: string | null }>;
  const images = useMemo(() => items.filter((i: any) => i?.type === "image"), [items]);
  const videos = useMemo(() => items.filter((i: any) => i?.type === "video"), [items]);

  const handleDownload = async (item: { id: number; type: "image" | "video"; filename: string }) => {
    // Use same-origin streaming endpoint so the browser can't decide to play the video inline.
    // This endpoint sets Content-Disposition: attachment and supports Range for large files.
    try {
      const href = `/api/purchased-download/${encodeURIComponent(token)}/${item.type}/${item.id}`;
      const a = document.createElement("a");
      a.href = href;
      a.download = item.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`Download gestartet: ${item.filename}`);
    } catch (e: any) {
      toast.error(`Download fehlgeschlagen: ${item.filename}`);
      console.error('Download failed', { item, error: e });
    }
  };

  const handleDownloadAll = async () => {
    const list = items.filter(Boolean) as any[];
    if (list.length === 0) return;
    toast.info(`Starte Download von ${list.length} Dateien…`);
    for (const it of list) {
      await handleDownload({ id: Number(it.id), type: it.type, filename: it.filename });
      await new Promise(r => setTimeout(r, 400));
    }
    toast.success(t.downloads.allDownloadsStarted);
  };

  if (!token) return null;

  if (validate.isLoading || links.isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Laden…</div>;
  }

  if (validate.isError || links.isError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card>
          <CardHeader>
            <CardTitle>Ungültiger oder abgelaufener Link</CardTitle>
            <CardDescription>Bitte kontaktiere den Fotografen für einen neuen Link.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b sticky top-0 bg-background z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold">Gekaufte Dateien</h1>
              <p className="text-sm text-muted-foreground">
                Bestellung #{validate.data?.orderNumber}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">gültig bis {new Date(validate.data!.expiresAt).toLocaleDateString()}</Badge>
            {items.length > 0 && (
              <Button onClick={handleDownloadAll}><Download className="h-4 w-4 mr-2"/>Alle herunterladen</Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-8">
        {videos.length > 0 && (
          <section>
            <h2 className="text-xl font-semibold mb-3 flex items-center gap-2"><VideoIcon className="h-5 w-5"/>Videos ({videos.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {videos.map((v: any) => (
                <Card key={`v-${v.id}`}>
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-20 h-20 rounded bg-muted overflow-hidden flex items-center justify-center shrink-0">
                        {v.thumbUrl ? (
                          <img src={v.thumbUrl} alt={v.filename} className="w-full h-full object-cover" />
                        ) : (
                          <div className="text-xs text-muted-foreground">No preview</div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate" title={v.filename}>{v.filename}</p>
                        <p className="text-xs text-muted-foreground">Video</p>
                      </div>
                    </div>
                    <Button size="sm" onClick={() => handleDownload({ id: Number(v.id), type: "video", filename: v.filename })}>
                      <Download className="h-4 w-4 mr-2"/>Download
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {images.length > 0 && (
          <section>
            <h2 className="text-xl font-semibold mb-3 flex items-center gap-2"><ImageIcon className="h-5 w-5"/>{`Bilder (${images.length})`}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {images.map((img: any) => (
                <Card key={`i-${img.id}`}>
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-20 h-20 rounded bg-muted overflow-hidden flex items-center justify-center shrink-0">
                        {img.previewUrl ? (
                          <img src={img.previewUrl} alt={img.filename} className="w-full h-full object-cover" />
                        ) : (
                          <div className="text-xs text-muted-foreground">No preview</div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate" title={img.filename}>{img.filename}</p>
                        <p className="text-xs text-muted-foreground">Original</p>
                      </div>
                    </div>
                    <Button size="sm" onClick={() => handleDownload({ id: Number(img.id), type: "image", filename: img.filename })}>
                      <Download className="h-4 w-4 mr-2"/>Download
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {items.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">Keine Dateien vorhanden</CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
